import React, { useState, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Wallet, DollarSign, History, RefreshCcw } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

// --- Constants ---


// --- Local Schema for Top-up Form ---
const TopUpSchema = z.object({
    amountMwk: z.number().min(1000, "Minimum top-up is MWK 1,000").max(1000000, "Maximum top-up is MWK 1,000,000"),
});



type TTopUpSchema = z.infer<typeof TopUpSchema>;

// --- Helper Component for Top-up Form ---
const TopUpForm: React.FC = () => {
    const [isPolling, setIsPolling] = useState(false);
    const [currentTxRef, setCurrentTxRef] = useState<string | null>(null);
    const utils = trpc.useUtils();
    const location = useLocation();

    const form = useForm<TTopUpSchema>({
        resolver: zodResolver(TopUpSchema),
        defaultValues: {
            amountMwk: 50000,
        },
    });

    // Check for pending payment from deep link (mobile app) OR local storage
    useEffect(() => {
        // Priority 1: Navigation State (from Deep Link)
        const stateTxRef = location.state?.txRef;
        // Priority 2: Local Storage (fallback)
        const pendingTxRef = localStorage.getItem('pending_payment_tx_ref');

        if (stateTxRef) {
            console.log('🔄 Payment flow detected via Deep Link:', stateTxRef);
            setCurrentTxRef(stateTxRef);
            setIsPolling(true);
            // Clean up state to prevent re-triggering on simple reloads
            window.history.replaceState({}, document.title);
        } else if (pendingTxRef) {
            console.log('🔄 Resuming payment verification from storage for:', pendingTxRef);
            setCurrentTxRef(pendingTxRef);
            setIsPolling(true);
            // Clear from localStorage
            localStorage.removeItem('pending_payment_tx_ref');
        }
    }, [location.state]);

    // Polling Verification
    trpc.wallet.verifyTransaction.useQuery(
        { txRef: currentTxRef! },
        {
            enabled: isPolling && !!currentTxRef,
            refetchInterval: 3000, // Poll every 3s
            onSuccess: async (data) => {
                if (data.status === 'success') {
                    setIsPolling(false);
                    setCurrentTxRef(null);
                    // Force close the In-App Browser if it's open
                    await Browser.close();
                    toast.success("Payment Received!", { description: "Your wallet has been credited." });
                    utils.wallet.getBalance.invalidate();
                    utils.wallet.getTransactions.invalidate();
                    form.reset();
                } else if (data.status === 'error') {
                    // Keep polling
                }
            }
        }
    );

    const topUpMutation = trpc.wallet.createTokenPurchaseLink.useMutation({
        onSuccess: (data) => {
            if (data.checkoutUrl) {
                // Standard: Open Browser AND Start Polling
                toast.info("Opening secure payment page...");
                // Use system browser to ensure deep links work correctly and don't get trapped in WebView
                Browser.open({ url: data.checkoutUrl, windowName: '_system' });
                // Start listening for the webhook/verification
                if (data.txRef) {
                    setCurrentTxRef(data.txRef);
                    setIsPolling(true);
                }
            } else {
                toast.error("Failed to initiate payment.");
            }
        },
        onError: (error) => {
            console.error('Top-up error details:', error);
            // Try to extract a meaningful message from the error object
            let message = error.message || "An unexpected error occurred.";
            if (message.includes('Prisma')) message = "System Error: Database connection failed.";

            toast.error("Top-up failed.", {
                description: message
            });
        },
    });

    const onSubmit = (data: TTopUpSchema) => {
        const platform = Capacitor.getPlatform() === 'web' ? 'web' : 'mobile';
        topUpMutation.mutate({ ...data, platform });
    };

    const isLoading = topUpMutation.isLoading || isPolling;

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                    control={form.control}
                    name="amountMwk"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-1">Top-up Amount (MWK) *</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="number"
                                        className="pl-10"
                                        placeholder="Min 50,000"
                                        {...field}
                                        value={field.value}
                                        onChange={(e) =>
                                            field.onChange(e.target.value === "" ? 0 : e.target.valueAsNumber)
                                        }
                                        disabled={isLoading}
                                    />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 relative overflow-hidden"
                    disabled={isLoading}
                >
                    {isLoading && (
                        <div className="absolute inset-0 bg-primary/50 flex items-center justify-center backdrop-blur-[1px]">
                            <Loader2 className="h-5 w-5 animate-spin text-white" />
                        </div>
                    )}
                    {isPolling ? 'Confirming Payment...' : 'Proceed to Pay'}
                </Button>
            </form>
        </Form>
    );
};

// --- Main Wallet Page Component ---
const WalletPage: React.FC = () => {
    // 1. Fetch Wallet Balance - Uses correct getBalance procedure
    const { data: wallet, isLoading: isLoadingBalance, error: errorBalance, refetch: refetchBalance } =
        trpc.wallet.getBalance.useQuery(undefined, { staleTime: 5000 });

    // 2. Fetch Transaction History - Uses correct getTransactions procedure
    const { data: transactions, isLoading: isLoadingHistory, error: errorHistory, refetch: refetchHistory } =
        trpc.wallet.getTransactions.useQuery(undefined, { staleTime: 60000 });

    const balance = wallet?.balance || 0;

    // Helper to format currency
    const formatCurrency = (amount: number) => `MWK ${amount.toLocaleString()}`;

    return (
        <div className="container mx-auto py-4 px-2 sm:py-12 sm:px-4 max-w-7xl animate-fade-in">
            <h1 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-8 flex items-center gap-3 sm:gap-4">
                <Wallet className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                My Transporter Wallet
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                {/* Wallet Balance Card (Col 1-2) */}
                <Card className="md:col-span-2 glass">
                    <CardHeader className="p-4 sm:p-6">
                        <CardTitle className="text-xl sm:text-2xl flex items-center justify-between">
                            Current Balance
                            <Button variant="ghost" size="icon" onClick={() => refetchBalance()} disabled={isLoadingBalance}>
                                <RefreshCcw className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoadingBalance ? 'animate-spin' : ''}`} />
                            </Button>
                        </CardTitle>
                        <CardDescription className="text-xs sm:text-sm">Your available funds for securing jobs. (1 MWK = 1 Balance Unit)</CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                        {isLoadingBalance ? (
                            <Skeleton className="h-10 sm:h-12 w-1/2" />
                        ) : errorBalance ? (
                            <p className="text-destructive text-sm">Error: Failed to load balance.</p>
                        ) : (
                            <p className="text-3xl sm:text-5xl font-extrabold text-green-500">
                                {formatCurrency(balance)}
                            </p>
                        )}
                        <Separator className="my-3 sm:my-4" />
                        <Link to="/dashboard/wallet/purchase-success" className="text-xs sm:text-sm text-muted-foreground hover:underline">
                            View pending purchases...
                        </Link>
                    </CardContent>
                </Card>

                {/* Top-up Form Card (Col 3) */}
                <Card className="glass">
                    <CardHeader className="p-4 sm:p-6">
                        <CardTitle className="text-lg sm:text-xl">Top-up Balance</CardTitle>
                        <CardDescription className="text-xs sm:text-sm">Minimum top-up is MWK 50,000.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                        <TopUpForm />
                    </CardContent>
                </Card>
            </div>

            <h2 className="text-xl sm:text-3xl font-bold mt-8 sm:mt-12 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <History className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                Transaction History
                <Button variant="ghost" size="icon" onClick={() => refetchHistory()} disabled={isLoadingHistory}>
                    <RefreshCcw className={`w-4 h-4 sm:w-5 sm:h-5 ${isLoadingHistory ? 'animate-spin' : ''}`} />
                </Button>
            </h2>

            {/* Transaction History Table */}
            <Card className="glass">
                <CardContent className="pt-6">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[200px]">Transaction Ref</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">Amount (MWK)</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-[180px]">Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoadingHistory ? (
                                    <TableRow><TableCell colSpan={5} className="h-24 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></TableCell></TableRow>
                                ) : errorHistory ? (
                                    <TableRow><TableCell colSpan={5} className="text-destructive text-center">Failed to load history: {errorHistory.message}</TableCell></TableRow>
                                ) : transactions && transactions.length > 0 ? (
                                    transactions.map((tx: any) => (
                                        <TableRow key={tx.id}>
                                            <TableCell className="font-mono text-xs w-[200px] whitespace-nowrap">
                                                {tx.paychanguRef || tx.id}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={tx.type === 'TOPUP' ? 'primary' : 'secondary'}>{tx.type}</Badge>
                                            </TableCell>
                                            <TableCell className={`text-right font-medium ${tx.amountMwk > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                                {formatCurrency(tx.amountMwk)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={tx.status === 'COMPLETED' ? 'success' : tx.status === 'PENDING' ? 'warning' : 'destructive'}>
                                                    {tx.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-xs">
                                                {format(new Date(tx.createdAt), 'MMM dd, yyyy HH:mm')}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow><TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No transactions found.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default WalletPage;